/**
 * A small WHATWG-style URL resolver. QuickJS has no `URL`, and both the
 * guest (fetch targets) and the engine (origins for analysis) need one.
 *
 * Covers what network sinks need: special (hierarchical) schemes with
 * relative resolution, default ports, dot segments, backslashes, and percent
 * encoding of spaces, quotes, and non-ASCII. Other schemes are kept opaque.
 * Not covered: IDNA/punycode hosts and IPv4 number normalization.
 */

const DEFAULT_PORTS: Record<string, string> = {
  "http:": "80",
  "https:": "443",
  "ws:": "80",
  "wss:": "443",
  "ftp:": "21",
};

const isSpecial = (protocol: string) => protocol in DEFAULT_PORTS;

interface Parts {
  protocol: string;
  /** `user:pw@`, or empty. */
  userinfo: string;
  /** Host without port, lowercased. */
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
}

const SCHEME = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;
const FORBIDDEN_HOST = /[\s#%/:<>?@[\\\]^|]/;

function percentEncode(s: string, keep: RegExp): string {
  let out = "";
  for (const ch of s) {
    out += keep.test(ch) ? ch : encodeURIComponent(ch);
  }
  return out;
}

// Characters outside these classes are percent-encoded (UTF-8).
const PATH_SAFE = /^[!$%&'()*+,\-./0-9:;=@A-Z[\]^_a-z{|}~]$/;
const QUERY_SAFE = /^[!$%&'()*+,\-./0-9:;=?@A-Z[\\\]^_`a-z{|}~]$/;
const FRAGMENT_SAFE = /^[!#$%&'()*+,\-./0-9:;=?@A-Z[\\\]^_a-z{|}~]$/;

function removeDotSegments(path: string): string {
  const out: string[] = [];
  const segments = path.split("/");
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i] ?? "";
    const last = i === segments.length - 1;
    if (seg === "." || seg.toLowerCase() === "%2e") {
      if (last) out.push("");
    } else if (seg === ".." || seg.toLowerCase() === ".%2e" || seg.toLowerCase() === "%2e.") {
      out.pop();
      if (last) out.push("");
    } else {
      out.push(seg);
    }
  }
  return `/${out.join("/")}`;
}

function splitTail(rest: string): { path: string; search: string; hash: string } {
  const hashAt = rest.indexOf("#");
  const hash = hashAt >= 0 ? rest.slice(hashAt) : "";
  const beforeHash = hashAt >= 0 ? rest.slice(0, hashAt) : rest;
  const queryAt = beforeHash.indexOf("?");
  return {
    path: queryAt >= 0 ? beforeHash.slice(0, queryAt) : beforeHash,
    search: queryAt >= 0 ? beforeHash.slice(queryAt) : "",
    hash,
  };
}

/** Parses an absolute URL with a special scheme; null if invalid. */
function parseSpecial(protocol: string, afterScheme: string): Parts | null {
  const rest = afterScheme.replace(/^[/\\]*/, "");
  const authorityEnd = rest.search(/[/\\?#]/);
  const authority = authorityEnd >= 0 ? rest.slice(0, authorityEnd) : rest;
  const tail = splitTail(authorityEnd >= 0 ? rest.slice(authorityEnd) : "");

  const at = authority.lastIndexOf("@");
  const userinfo = at >= 0 ? `${authority.slice(0, at)}@` : "";
  const hostPort = at >= 0 ? authority.slice(at + 1) : authority;
  const portMatch = /:(\d*)$/.exec(hostPort);
  const hostname = (portMatch ? hostPort.slice(0, portMatch.index) : hostPort).toLowerCase();
  const rawPort = portMatch?.[1] ?? "";

  if (hostname === "") return null;
  if (!hostname.startsWith("[") && FORBIDDEN_HOST.test(hostname)) return null;
  if (rawPort !== "" && Number(rawPort) > 65535) return null;
  const port = rawPort === "" || rawPort === DEFAULT_PORTS[protocol] ? "" : String(Number(rawPort));

  const path = tail.path.replace(/\\/g, "/");
  return {
    protocol,
    userinfo,
    hostname,
    port,
    pathname: removeDotSegments(percentEncode(path === "" ? "/" : path, PATH_SAFE)),
    search: percentEncode(tail.search, QUERY_SAFE),
    hash: percentEncode(tail.hash, FRAGMENT_SAFE),
  };
}

const format = (p: Parts) =>
  `${p.protocol}//${p.userinfo}${p.hostname}${p.port ? `:${p.port}` : ""}${p.pathname}${p.search}${p.hash}`;

function parseAbsolute(url: string): Parts | null {
  const scheme = SCHEME.exec(url);
  if (!scheme?.[1]) return null;
  const protocol = `${scheme[1].toLowerCase()}:`;
  return isSpecial(protocol) ? parseSpecial(protocol, url.slice(scheme[0].length)) : null;
}

/**
 * Resolves `input` against `base` like `new URL(input, base).href`; returns
 * null where the URL constructor would throw.
 */
export function resolveUrl(input: string, base: string): string | null {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: WHATWG strips C0 controls
  const url = input.replace(/^[\u0000- ]+|[\u0000- ]+$/g, "").replace(/[\t\n\r]/g, "");
  const scheme = SCHEME.exec(url);
  if (scheme?.[1]) {
    const protocol = `${scheme[1].toLowerCase()}:`;
    if (!isSpecial(protocol)) return protocol + url.slice(scheme[0].length);
    const parts = parseSpecial(protocol, url.slice(scheme[0].length));
    return parts ? format(parts) : null;
  }

  const b = parseAbsolute(base);
  if (!b) return null;
  if (/^[/\\]{2}/.test(url)) {
    const parts = parseSpecial(b.protocol, url);
    return parts ? format(parts) : null;
  }
  const tail = splitTail(url);
  let pathname = b.pathname;
  let search = b.search;
  if (tail.path !== "") {
    const path = tail.path.replace(/\\/g, "/");
    pathname = path.startsWith("/")
      ? path
      : `${b.pathname.slice(0, b.pathname.lastIndexOf("/") + 1)}${path}`;
    search = tail.search;
  } else if (tail.search !== "") {
    search = tail.search;
  }
  return format({
    ...b,
    pathname: removeDotSegments(percentEncode(pathname, PATH_SAFE)),
    search: percentEncode(search, QUERY_SAFE),
    hash: percentEncode(tail.hash, FRAGMENT_SAFE),
  });
}

export interface UrlParts {
  protocol: string;
  /** Lowercased, without the port. */
  hostname: string;
  /** Empty for the scheme's default port. */
  port: string;
  pathname: string;
  search: string;
  hash: string;
}

/** The parts of an absolute URL with a special scheme (http, https, ws, …); null otherwise. */
export function parseUrl(url: string): UrlParts | null {
  const p = parseAbsolute(url);
  if (!p) return null;
  const { userinfo: _, ...parts } = p;
  return parts;
}

/** The serialized origin of an absolute URL, or `"null"` for opaque/invalid ones. */
export function originOf(url: string): string {
  const p = parseAbsolute(url);
  if (!p || p.protocol === "ftp:") return "null";
  return `${p.protocol}//${p.hostname}${p.port ? `:${p.port}` : ""}`;
}
